import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Upload, Trash2, Loader2, ImageIcon, Sparkles } from 'lucide-react';
import { fetchSettings, uploadLogo, removeLogo, resolveImageUrl, formatApiError } from '../../services/api';

const LogoManager = () => {
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const s = await fetchSettings();
      setLogoUrl(s?.logo_url || '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const result = await uploadLogo(file);
      setLogoUrl(result.logo_url || result.url);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove current logo? The default Sparkles icon will be shown.')) return;
    try {
      await removeLogo();
      setLogoUrl('');
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Site Logo</h2>
        <p className="text-amber-100/60 text-sm mt-1">Appears in the site header on every page</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Current logo preview */}
        <div className="p-6 bg-emerald-950/40 border border-amber-600/30 rounded-lg">
          <Label className="text-amber-300 text-xs uppercase tracking-wider mb-3 block">Current Logo</Label>
          {loading ? (
            <p className="text-amber-100/60">Loading...</p>
          ) : logoUrl ? (
            <div className="space-y-4">
              <div className="bg-[#0a1612] p-6 rounded-lg flex items-center justify-center border border-amber-600/30">
                <img
                  src={resolveImageUrl(logoUrl)}
                  alt="Current logo"
                  className="max-h-32 max-w-full object-contain"
                  data-testid="logo-preview"
                />
              </div>
              <Button
                onClick={handleRemove}
                className="bg-red-900/60 text-red-300 hover:bg-red-900 border border-red-500/30 w-full"
                data-testid="logo-remove-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Logo
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-[#0a1612] p-6 rounded-lg flex flex-col items-center justify-center border border-dashed border-amber-600/30">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-700 to-amber-600 rounded-lg flex items-center justify-center mystical-glow-gold border border-amber-500/40 mb-3">
                  <Sparkles className="w-9 h-9 text-amber-100" />
                </div>
                <p className="text-amber-100/60 text-sm text-center">No custom logo set</p>
                <p className="text-amber-100/40 text-xs text-center mt-1">Using default Sparkles icon</p>
              </div>
            </div>
          )}
        </div>

        {/* Upload box */}
        <div className="p-6 bg-emerald-950/40 border border-amber-600/30 rounded-lg">
          <Label className="text-amber-300 text-xs uppercase tracking-wider mb-3 block">Upload New Logo</Label>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />

          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className="border-2 border-dashed border-amber-600/40 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-emerald-900/20 transition"
            data-testid="logo-upload-dropzone"
          >
            {uploading ? (
              <Loader2 className="w-10 h-10 text-amber-400 mx-auto mb-3 animate-spin" />
            ) : (
              <ImageIcon className="w-10 h-10 text-amber-400/70 mx-auto mb-3" />
            )}
            <p className="text-amber-200 font-medium mb-1" style={{ fontFamily: 'Cinzel, serif' }}>
              {uploading ? 'Uploading...' : 'Click to choose file'}
            </p>
            <p className="text-amber-100/50 text-xs">PNG, JPG, WEBP or SVG (square works best) — max 8 MB</p>
          </div>

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-gold w-full mt-4"
            data-testid="logo-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Choose Image'}
          </Button>

          {error ? <div className="mt-4 p-3 rounded bg-red-950/60 border border-red-500/40 text-red-300 text-sm">{error}</div> : null}
        </div>
      </div>
    </div>
  );
};

export default LogoManager;
